import React, { useMemo, useEffect, useState } from 'react';
import { ComparisonListStatus } from '@navi-toolbox/ui-components';
import {
  getDisplayedName,
  getPrimaryPrice,
  getProductBelong,
  getProductId,
  getProductPriceCategory,
} from 'features/product-info/ducks/selectors';
import { useTranslation } from 'react-i18next';
import { SnackbarProvider } from 'fronton-react';
import { useBreakpoints } from 'common/hooks';
import { analyticsUtils } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { getCompareCategory } from 'features/product-info';
import {
  getCompareListsError,
  getIsInComparison,
  getProductsCompareLists,
  productsCompareListsActions,
} from 'features/products-compare';
import { v4 as uuidv4 } from 'uuid';
import { getLinkedPagesBrand } from 'features/linked-pages';
import { getProductsCompareListLimit, getProductsCompareListsLimit } from 'common/selectors';
import { StatusConfig, StatusType } from './types';
import { GA_ADD_TO_COMPARISON_LIST, GA_REMOVE_FROM_COMPARISON_LIST } from './constants';
import { getMode } from './utils';
import { CompareButtonWithTooltip } from './compare-button-with-tooltip';

export const PdpComparison: React.FC = () => {
  const dispatch = useDispatch();
  const breakpoints = useBreakpoints();
  const isDesktop = useMemo(() => breakpoints?.largeDesktop || breakpoints?.desktop, [breakpoints]);
  const { t } = useTranslation();
  const compareCategory = useSelector(getCompareCategory);
  const compareLists = useSelector(getProductsCompareLists) || [];
  const compareListLimit = useSelector(getProductsCompareListLimit);
  const compareListsLimit = useSelector(getProductsCompareListsLimit);
  const groupId = compareCategory?.familyId || null;
  const productId = useSelector(getProductId);
  const productName = useSelector(getDisplayedName);
  const { price, prevPrice, discountPercent } = useSelector(getPrimaryPrice);
  const isActiveFromStore = useSelector(getIsInComparison(groupId, productId || ''));
  const error = useSelector(getCompareListsError);
  const { name: brandName } = useSelector(getLinkedPagesBrand) || {};
  const productBelong = useSelector(getProductBelong);
  const productTag = useSelector(getProductPriceCategory);

  const [optimisticIsActive, setOptimisticIsActive] = useState(isActiveFromStore);
  const [showStatus, setShowStatus] = useState(false);
  const [statusType, setStatusType] = useState<StatusType>('added');
  const [statusId, setStatusId] = useState(uuidv4());

  useEffect(() => {
    setOptimisticIsActive(isActiveFromStore);
  }, [isActiveFromStore]);

  const handleAddToCompare = () => {
    const currentGroup = compareLists?.find((list) => list.groupId === groupId);
    const isProductListCountInvalid = currentGroup && currentGroup.products.length >= compareListLimit;
    const isListsCountInvalid = !currentGroup && compareLists.length >= compareListsLimit;
    if (!compareLists.length || !(isProductListCountInvalid || isListsCountInvalid)) {
      setOptimisticIsActive(true);
      dispatch(productsCompareListsActions.SEND_PRODUCTS_COMPARE_LIST_ACTION({ action: 'add' }));
      setStatusType(error ? 'error' : 'added');
    } else {
      if (isProductListCountInvalid) {
        setStatusType('productListCountAlert');
      }
      if (isListsCountInvalid) {
        setStatusType('listCountAlert');
      }
    }
    setShowStatus(true);
  };

  const handleRemoveFromCompare = () => {
    setOptimisticIsActive(false);
    dispatch(productsCompareListsActions.SEND_PRODUCTS_COMPARE_LIST_ACTION({ action: 'delete' }));
    setStatusType(error ? 'error' : 'removed');
    setShowStatus(true);
  };

  const onClick = () => {
    setStatusId(uuidv4());
    analyticsUtils.sendEvent(
      optimisticIsActive ? GA_REMOVE_FROM_COMPARISON_LIST : GA_ADD_TO_COMPARISON_LIST,
      {
        elementType: 'button',
        elementBlock: 'productDetail',
        elementName: 'comparisonList',
        category: compareCategory?.name,
        brand: brandName,
        productName,
        productId,
        productDiscount: discountPercent,
        productPrice: price?.toFixed(2),
        productPrevPrice: prevPrice ? prevPrice.toFixed(2) : null,
        productBelong: productBelong,
        productTag: productTag,
      },
    );
    return optimisticIsActive ? handleRemoveFromCompare() : handleAddToCompare();
  };
  const handleMoveToComparePage = () => {
    setShowStatus(false);
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'productDetail / pop-up',
      elementName: 'comparisonList',
      elementText: 'Перейти',
    });
  };
  const redirectTo =
    statusType === 'added' || statusType === 'productListCountAlert' || statusType === 'listCountAlert'
      ? '/compare/'
      : undefined;
  const buttonText = optimisticIsActive ? t('ui.buttons.compareAdded') : t('ui.buttons.compareAdd');
  const tipText = optimisticIsActive
    ? t('ui.buttons.compare-tooltip-remove')
    : t('ui.buttons.compare-tooltip-add');
  const statusConfig: StatusConfig = {
    added: {
      text: t('ui.snackbar.compareAddedText'),
      buttonText: t('ui.snackbar.moveTo'),
      action: handleMoveToComparePage,
    },
    removed: {
      text: t('ui.snackbar.compareRemovedText'),
      buttonText: t('ui.snackbar.dismiss'),
      action: onClick,
    },
    error: {
      text: t('ui.snackbar.compareErrorText'),
      buttonText: t('ui.snackbar.tryAgain'),
      action: () => {
        setShowStatus(false);
        onClick();
      },
    },
    productListCountAlert: {
      header: t('ui.snackbar.compareProductListAlertHeader'),
      text: t('ui.snackbar.compareAlertText'),
      buttonText: t('ui.snackbar.moveToList'),
      action: handleMoveToComparePage,
    },
    listCountAlert: {
      header: t('ui.snackbar.compareListsAlertHeader'),
      text: t('ui.snackbar.compareAlertText'),
      buttonText: t('ui.snackbar.moveToList'),
      action: handleMoveToComparePage,
    },
  };
  const currentStatusConfig = statusConfig[statusType];
  const [hasSentErrorEvent, setHasSentErrorEvent] = useState(false);
  useEffect(() => {
    if (error) {
      setStatusType('error');
      setShowStatus(true);
      if (!hasSentErrorEvent) {
        analyticsUtils.sendEvent('GA:show', {
          elementType: 'block',
          elementBlock: 'productDetail',
          elementName: 'comparisonList',
          elementText: `error / ${currentStatusConfig.text}`,
        });
        setHasSentErrorEvent(true);
      }
    } else {
      setHasSentErrorEvent(false);
    }
  }, [error, currentStatusConfig.text, hasSentErrorEvent]);

  return (
    <>
      {showStatus && (
        <SnackbarProvider>
          <ComparisonListStatus
            id={statusId}
            hrefForButton={redirectTo}
            mode={getMode(statusType)}
            header={currentStatusConfig.header}
            text={currentStatusConfig.text}
            buttonText={currentStatusConfig.buttonText}
            handleClickButton={currentStatusConfig.action}
          />
        </SnackbarProvider>
      )}
      <CompareButtonWithTooltip
        active={optimisticIsActive}
        onClick={onClick}
        buttonText={buttonText}
        tipText={tipText}
        isDesktop={!!isDesktop}
      />
    </>
  );
};
